// export function getFullDomPath(el) {
//   var stack = [];
//   while (el.parentNode !== null) {
//     var sibCount = 0;
//     var sibIndex = 0;
//     for (var i = 0; i < el.parentNode.childNodes.length; i++) {
//       var sib = el.parentNode.childNodes[i];
//       if (sib.nodeName === el.nodeName) {
//         if (sib === el) {
//           sibIndex = sibCount;
//         }
//         sibCount++;
//       }
//     }
//     if (el.hasAttribute("id") && el.id !== "") {
//       stack.unshift(el.nodeName.toLowerCase() + "#" + el.id);
//     } else if (sibCount > 1) {
//       stack.unshift(el.nodeName.toLowerCase() + ":eq(" + sibIndex + ")");
//     } else {
//       stack.unshift(el.nodeName.toLowerCase());
//     }
//     el = el.parentNode;
//   }
//
//   return stack.slice(1).join(" > "); // removes the html element
// }

import { toast } from "react-toastify";

/**
 * Builds a proper image URL from an activity screenshot path.
 * Handles both base64 data URLs (from new captures) and relative paths (from saved activities).
 * @param imgPath - The image path (can be base64 data URL or relative path)
 * @returns Full URL to the image
 */
export const getActivityImageUrl = (imgPath: string | undefined | null): string => {
  if (!imgPath) return '';
  // If it's already a base64 data URL, return as-is
  if (imgPath.startsWith('data:')) {
    return imgPath;
  }
  // If it's already a full URL, return as-is
  if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
    return imgPath;
  }
  // Otherwise, prepend the server URL
  return `${process.env.REACT_APP_SERVER_URL}${imgPath}`;
};

export const getUniqueID = (name: string = "label"): string => `${name}-${Math.random() * 100000}`;

export const stringForTrim = (value: string): string => (value || "").trim();

export const convertToString = (needConvert: any): string => JSON.stringify(needConvert);

export const convertToSourceState = <T>(needConvert: string): T => JSON.parse(needConvert);

export const deepCloning = <T>(value: T): T => convertToSourceState(convertToString(value));

export const convertObjectToArray = <T>(arr: T | T[]): T[] => {
  if (!Array.isArray(arr)) {
    return Object.keys(arr as any).length !== 0 ? [arr] : [];
  }
  return arr;
};

type ErrorResponse = {
  error?: {
    description?: string;
    code?: number;
  };
  message?: string;
};

export const showErrMessage = (res: ErrorResponse): void => {
  if (
    res.error !== undefined &&
    res.error !== null &&
    res.error.description !== undefined &&
    res.error.description !== null
  ) {
    // Only redirect to login for 401 Unauthorized errors
    if (res.error.code === 401) {
      window.location.href = "/login";
      return; // Don't show toast if redirecting
    }
    toast.error((res.error && res.error.description) || "Something went wrong.");
  } else if (res.message !== undefined && res.message !== null) {
    toast.error(res.message);
  } else {
    toast.error("Something went wrong");
  }
};

type SelectOption = {
  label: string;
  value: string;
};

export const getSelectorOptions = (): SelectOption[] => {
  return [
    // { label: "Text-Label on the element", value: "textLabel" },
    { label: "Container ID", value: "containerId" },
    { label: "Container Class", value: "containerClasses" },
    { label: "Class of Element on which action is to be done", value: "classes" },
    { label: "ID of Element on which action is to be done", value: "elementId" },
    // { label: "Value of Attribute of Element", value: "elementType" },
    // { label: "Text on the element", value: "text" }
    { label: "Element Type", value: "elementType" },
    { label: "Name Identifier of the element", value: "name" }
  ];
};

type SelectedOption = {
  name: string;
  value: string | null;
};

export const getSelectorOptionsSelected = (SelectedOpt: SelectedOption[]): SelectOption[] => {
  const list = getSelectorOptions();

  var listFinal = list
    .filter(function(o1) {
      return SelectedOpt.some(function(o2) {
        return o1.value === o2.name && o2.value !== null && o2.value.length !== 0; // assumes unique id
      });
    })
    .map(function(allVal) {
      return allVal;
    });
  return listFinal;
};

export const getSelectorOptionsAC = (): SelectOption[] => {
  return [
    // { label: "Text-Label on the element", value: "textLabel" },
    { label: "Container ID", value: "containerId" },
    { label: "Container Class", value: "containerClasses" },
    { label: "Class of Element on which action is to be done", value: "classes" },
    { label: "ID of Element on which action is to be done", value: "elementId" },
    { label: "Element Type", value: "elementType" },
    { label: "Option Classes", value: "optionClasses" },
    { label: "Option ID", value: "optionId" },
    { label: "Option Name", value: "optionName" }
  ];
};

export const getSelectorOptionsSelectedAC = (SelectedOpt: SelectedOption[]): SelectOption[] => {
  const list = getSelectorOptionsAC();

  var listFinal = list
    .filter(function(o1) {
      return SelectedOpt.some(function(o2) {
        return o1.value === o2.name && o2.value !== null && o2.value.length !== 0; // assumes unique id
      });
    })
    .map(function(allVal) {
      return allVal;
    });
  return listFinal;
};

type ActivityTypeOption = {
  label: string;
  value: string;
  showColumnName: boolean;
  showColumnValue: boolean;
  isDate?: boolean;
};

type ManualActivityGroup = {
  label: string;
  options: ActivityTypeOption[];
};

export const getManualActivityTypes = (): ManualActivityGroup[] => {
  return [
    {
      label: "Input",
      options: getActivityTypesInput()
    }
  ];
};

export const getActivityTypesInput = (): ActivityTypeOption[] => {
  return [
    { label: "Enter a Text Input", value: "Input Text", showColumnName: true, showColumnValue: true },
    { label: "Enter a Menu Hover Path", value: "MenuUIHover", showColumnName: true, showColumnValue: true },
    { label: "Fill an Auto complete", value: "AutoComplete", showColumnName: true, showColumnValue: true },
    { label: "Select a Checkbox", value: "Input Checkbox", showColumnName: true, showColumnValue: true },
    { label: "Select a Radio", value: "Input Radio", showColumnName: true, showColumnValue: true },
    { label: "Multi select a Dropdown", value: "Multiselect", showColumnName: true, showColumnValue: true },
    { label: "Select a dropdown", value: "Input Select", showColumnName: true, showColumnValue: true },
    { label: "Enter a Date", value: "Date", showColumnName: false, showColumnValue: false, isDate: true },
    { label: "Click on any WebElement", value: "Click", showColumnName: false, showColumnValue: false },
    { label: "Perform a Navigation Click", value: "NavigateClick", showColumnName: false, showColumnValue: false },
    { label: "Alert - Accept", value: "BrowserAlert", showColumnName: false, showColumnValue: false, hideLocator: true },
    { label: "Alert - Confirm", value: "BrowserConfirm", showColumnName: false, showColumnValue: true, hideLocator: true },
    { label: "Alert - Prompt", value: "BrowserPrompt", showColumnName: false, showColumnValue: true, hideLocator: true },
    {
      label: "Click to Open a new Window/Browser",
      value: "BrowserOpen",
      showColumnName: false,
      showColumnValue: false,
      hideLocator: true
    },
    { label: "Right Click", value: "RightClick", showColumnName: false, showColumnValue: false },
    { label: "Perform a Menu hover selection", value: "ElementHover", showColumnName: true, showColumnValue: true },
    { label: "Perform a droppable action", value: "Drop", showColumnName: false, showColumnValue: false },
    { label: "Perform a draggable action", value: "Drag", showColumnName: false, showColumnValue: false },
    { label: "Perform a resizable action", value: "Resizable", showColumnName: false, showColumnValue: false },
    { label: "Read a tool tip", value: "tooltip", showColumnName: false, showColumnValue: false }
  ];
};

export const getActivityTypes = (): string[] => {
  return [
    "INPUT",
    "Input Text",
    "Input Textarea",
    "MenuUIHover",
    "Input Image",
    "Input Reset",
    "Input Password",
    "Input Time",
    "Input Date",
    "Input DateTime",
    "Input DateTimeLocal",
    "Input Week",
    "Input Month",
    "Input Checkbox",
    "Input Radio",
    "Input Select Multiple",
    "Input Select",
    "InputButton",
    "Input Submit",
    "BrowserConfirm",
    "BrowserPrompt",
    "BrowserAlert",
    "BrowserOpen",
    "text",
    "Date",
    "OpenCalendar",
    "SelectDate",
    "Click",
    "mouse-hover",
    "ElementHover",
    "Wait",
    "NavigationClick",
    "LinkBlank",
    "Link",
    "Button",
    "Sort",
    "DoubleClick",
    "KeyPress",
    "Resizable",
    "Spinner",
    "Slider",
    "Selectable",
    "RightClick",
    "Drop",
    "Drag",
    "tooltip",
    "NavigateClick",
    "iFrame",
    "Alert",
    "BrowserConfirm",
    "BrowserPrompt",
    "Multiselect",
    "Window",
    "NewComponent",
    "JavaScript",
    "MenuItemClick",
    "MenuLabelHover",
    "MDBD Menu",
    "AutoComplete",
    "Authentication",
    "SelectableOptionColumnName",
    "existence",
    "foreach",
    "loop",
    "if",
    "ifelse",
    "while",
    "dowhile",
    "selectable-multiselect",
    "BrowserActivity",
    "Tooltip",
    "captcha-capture",
    "FileUpload",
    "DataExtractor",
    "DataValidator",
    "auth",
    "ws",
    "validate",
    "ActivityDynamic",
    "Web Table",
    "Input Date Time Local",
    "MessageStore",
    "Enhance Wait",
    "download",
    "mathEvaluator"
  ];
};

type Extras = {
  maxlength?: number | null;
  minlength?: number | null;
  pattern?: string | null;
  required?: boolean;
  [key: string]: any;
};

export type ValidationMetadata = {
  maxLength?: number | null;
  minLength?: number | null;
  pattern?: string | null;
  required?: boolean;
};

export const extractValidationMetadata = (
  extras: Extras | null | undefined
): ValidationMetadata => {
  if (!extras || typeof extras !== "object") {
    return {};
  }

  const out: ValidationMetadata = {};

  if (extras.maxlength !== null && extras.maxlength !== undefined) {
    const n = Number(extras.maxlength);
    if (!Number.isNaN(n)) out.maxLength = n;
  }

  if (extras.minlength !== null && extras.minlength !== undefined) {
    const n = Number(extras.minlength);
    if (!Number.isNaN(n)) out.minLength = n;
  }

  if (extras.pattern !== null && extras.pattern !== undefined) {
    out.pattern = String(extras.pattern);
  }

  if (extras.required === true) {
    out.required = true;
  }

  return out;
};
